/**
 * Role-Based Access Control (RBAC) Configuration
 * ================================================
 * Defines permissions for each user type:
 *   - admin: Full access
 *   - After Sales: Limited access (view+edit customers, bookings, registry; view-only payments, reports, units, unconfirm payments)
 *   - Accountant: Mostly view-only, can confirm unconfirmed payments
 */

// Helper to get user type from localStorage
export const getUserType = () => {
    try {
        const session = JSON.parse(localStorage.getItem("user_session"));
        return session?.type || "";
    } catch (e) {
        return "";
    }
};

// Normalize user type for comparison (case-insensitive)
const normalizeType = (type) => {
    if (!type) return "";
    const lower = type.toLowerCase().trim();
    if (lower === "admin") return "admin";
    if (lower === "after sales") return "after_sales";
    if (lower === "accountant") return "accountant";
    return lower;
};

/**
 * Permission definitions per role
 * Each key is a feature/page, and the value contains:
 *   - view: can the user see this page/section
 *   - edit: can the user edit/modify data
 *   - confirm: can the user confirm (specific to unconfirm payments)
 */
const PERMISSIONS = {
    admin: {
        dashboard: { view: true, edit: true },
        customers: { view: true, edit: true },
        bookings: { view: true, edit: true },
        reports: { view: true, edit: true },
        payments: { view: true, edit: true },
        unconfirm_payments: { view: true, edit: true, confirm: true },
        properties: { view: true, edit: true },
        units: { view: true, edit: true },
        registry: { view: true, edit: true },
        cheques: { view: true, edit: true },
        demands: { view: true, edit: true },
        settings: { view: true, edit: true },
    },
    after_sales: {
        dashboard: { view: true, edit: false },
        customers: { view: true, edit: true },
        bookings: { view: true, edit: true },
        reports: { view: true, edit: false },
        payments: { view: true, edit: false },
        unconfirm_payments: { view: true, edit: false, confirm: false },
        properties: { view: false, edit: false },
        units: { view: true, edit: false },
        registry: { view: true, edit: true },
        cheques: { view: false, edit: false },
        demands: { view: false, edit: false },
        settings: { view: false, edit: false },
    },
    accountant: {
        dashboard: { view: true, edit: false },
        customers: { view: true, edit: false },
        bookings: { view: true, edit: false },
        reports: { view: true, edit: false },
        payments: { view: true, edit: false },
        unconfirm_payments: { view: true, edit: false, confirm: true },
        properties: { view: false, edit: false },
        units: { view: false, edit: false },
        registry: { view: true, edit: false },
        cheques: { view: true, edit: true },
        demands: { view: true, edit: true },
        settings: { view: false, edit: false },
    },
};

/**
 * Get permission for a specific feature
 * @param {string} userType - The user's type from the session
 * @param {string} feature - The feature key (e.g., 'customers', 'bookings')
 * @returns {{ view: boolean, edit: boolean, confirm?: boolean }}
 */
export const getPermission = (userType, feature) => {
    const role = normalizeType(userType);
    const rolePerms = PERMISSIONS[role];

    // If role not found in permissions, default to no access
    if (!rolePerms) {
        return { view: false, edit: false, confirm: false };
    }

    return rolePerms[feature] || { view: false, edit: false, confirm: false };
};

/**
 * Check if a user can view a specific feature
 */
export const canView = (userType, feature) => {
    return getPermission(userType, feature).view;
};

/**
 * Check if a user can edit/modify in a specific feature
 */
export const canEdit = (userType, feature) => {
    return getPermission(userType, feature).edit;
};

/**
 * Check if a user can confirm (specific to unconfirm payments)
 */
export const canConfirm = (userType, feature) => {
    return getPermission(userType, feature).confirm === true;
};

/**
 * Check if user is admin
 */
export const isAdmin = (userType) => {
    return normalizeType(userType) === "admin";
};

/**
 * Get sidebar menu items filtered by user permissions
 */
export const getMenuItems = (userType) => {
    const allMenuItems = [
        { label: "Dashboard", path: "/dashboard", icon: "📊", feature: "dashboard" },
        { label: "Bookings", path: "/dashboard/bookings", icon: "📋", feature: "bookings" },
        { label: "Reports", path: "/dashboard/reports", icon: "📈", feature: "reports" },
        { label: "Payments", path: "/dashboard/payments", icon: "💰", feature: "payments" },
        { label: "Unconfirm Payments", path: "/dashboard/unconfirm-payments", icon: "📋", feature: "unconfirm_payments" },
        { label: "Cheques", path: "/dashboard/cheques", icon: "🏦", feature: "cheques" },
        { label: "Demands", path: "/dashboard/demands", icon: "🧾", feature: "demands" },
        { label: "Properties", path: "/dashboard/properties", icon: "🏠", feature: "properties" },
        { label: "Units", path: "/dashboard/units", icon: "🏢", feature: "units" },
        { label: "Customers", path: "/dashboard/customers", icon: "👥", feature: "customers" },
        { label: "Registry", path: "/dashboard/registry", icon: "📝", feature: "registry" },
        { label: "Settings", path: "/dashboard/settings", icon: "⚙️", feature: "settings" },
    ];

    return allMenuItems.filter((item) => canView(userType, item.feature));
};
